// Subscribes to the browser `document.fonts` FontFaceSet (the `loadingdone`
// event) — a browser API with no editor-level equivalent, so it is bound
// directly rather than through the typed bus. Documented exception per the
// criteria doc, section "Centralized typed event bus" (same class of exception
// as the zoom plugin's mouse-wheel binding). It never binds `canvas.on(...)`.
import { fabric } from 'fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

export interface FontLoadingFacade {
  /**
   * Re-measure and repaint every text object on the canvas so a font that has
   * just finished loading replaces the fallback it was first rendered with.
   *
   * Fabric measures + rasterizes text once and caches it; when a web font loads
   * asynchronously (a moment after the family was assigned), nothing re-renders,
   * so the text stays stuck in the fallback (Arial) until some later mutation
   * forces a repaint. This drops fabric's glyph-width cache
   * (`fabric.charWidthsCache`, keyed by family only, so the fallback widths
   * measured before the load would otherwise survive every re-measure and a
   * textbox keeps wrapping as if the fallback font were still in use), walks
   * the object tree, re-runs `initDimensions()` on
   * text, invalidates each text object's (and its parent groups') render cache,
   * and requests a repaint — **without** emitting `object:modified`, so a
   * background font load never flags the template as having unsaved changes.
   *
   * It intentionally does not know about business object kinds: a placeholder's
   * precise re-fit (font size chosen to fill its box against the real font
   * metrics) is the placeholder plugin's job, done on the `fonts:loaded` event
   * this plugin emits right after calling `refresh()`.
   */
  refresh(): void;
  /**
   * Resolve once the given font specs (CSS `font` shorthand, e.g.
   * `700 16px "Oswald"`) have loaded — or, with no args, once all currently
   * pending font loading has settled (`document.fonts.ready`). The headless
   * render pipeline awaits this before it measures text and snapshots the
   * canvas, so an export fired before the web fonts arrive stops baking the
   * fallback into the image. A spec that fails to load is swallowed rather than
   * rejecting the whole wait.
   */
  whenReady(specs?: string[]): Promise<void>;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    fonts?: FontLoadingFacade;
  }
}

/** Fabric type strings whose objects carry text metrics worth re-measuring. */
const TEXT_TYPES = new Set(['text', 'i-text', 'textbox']);

type MaybeContainer = fabric.Object & {
  initDimensions?: () => void;
  getObjects?: () => fabric.Object[];
  _objects?: fabric.Object[];
};

/**
 * Recursively invalidate one object: re-measure text, mark render caches dirty,
 * and descend into groups / active selections so nested text (e.g. a
 * placeholder group's inner textbox) is re-measured too.
 */
function touch(obj: fabric.Object): void {
  const node = obj as MaybeContainer;
  if ((TEXT_TYPES.has(obj.type ?? '') || typeof node.initDimensions === 'function') && node.initDimensions) {
    node.initDimensions();
    obj.setCoords();
  }
  obj.dirty = true;
  const children = node.getObjects?.() ?? node._objects;
  if (children) {
    for (const child of children) touch(child);
  }
}

const readDocumentFonts = (): FontFaceSet | undefined => (typeof document !== 'undefined' ? document.fonts : undefined);

export function fontLoadingPlugin(): EditorPlugin {
  return {
    name: FabricPluginName.FONT_LOADING,
    install(editor: FabricEditor) {
      const refresh = (): void => {
        // Whole cache, not per family: a Google font arrives as several
        // unicode-range faces (Latin first, Cyrillic only once Cyrillic is
        // drawn), and the widths of every family measured early are suspect.
        fabric.util.clearFabricFontCache();
        for (const obj of editor.canvas.getObjects()) touch(obj);
        editor.canvas.requestRenderAll();
      };

      const whenReady = (specs?: string[]): Promise<void> => {
        const fonts = readDocumentFonts();
        if (!fonts) return Promise.resolve();
        const loads = (specs ?? []).map((spec) => fonts.load(spec).catch(() => [] as FontFace[]));
        return Promise.all([fonts.ready, ...loads]).then(() => undefined);
      };

      const facade: FontLoadingFacade & { __off?: () => void } = { refresh, whenReady };
      editor.fonts = facade;

      const fonts = readDocumentFonts();
      // The DOM lib types FontFaceSet's 'loadingdone' listener as a plain Event,
      // so accept Event and narrow to read the loaded FontFaces.
      const onLoadingDone = (event: Event): void => {
        refresh();
        const fontfaces = (event as FontFaceSetLoadEvent).fontfaces ?? [];
        const fontFamilies = Array.from(new Set(fontfaces.map((f) => f.family.replace(/^["']|["']$/g, ''))));
        editor.emit('fonts:loaded', { fontFamilies });
      };
      fonts?.addEventListener('loadingdone', onLoadingDone);
      facade.__off = () => fonts?.removeEventListener('loadingdone', onLoadingDone);
    },
    dispose(editor: FabricEditor) {
      const facade = editor.fonts as (FontLoadingFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.fonts = undefined;
    },
  };
}
